import { useEffect } from 'react';

function isElementObstructed(el: HTMLElement) {
  const rect = el.getBoundingClientRect();
  const visualViewportHeight = window.visualViewport
    ? window.visualViewport.height
    : window.innerHeight;
  return rect.bottom > visualViewportHeight;
}

function scrollToInput(input: HTMLElement) {
  if (isElementObstructed(input)) {
    requestAnimationFrame(() => {
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }
}

function handleFocus(event: FocusEvent) {
  const input = event.target as HTMLElement;
  if (input.tagName === 'INPUT' || input.tagName === 'TEXTAREA') {
    scrollToInput(input);
  }
}

function handleViewportChange() {
  const activeElement = document.activeElement as HTMLElement;
  if (
    activeElement &&
    (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')
  ) {
    scrollToInput(activeElement);
  }
}

/**
 * Automatically scrolls an input or textarea element into view when it gains focus,
 * ensuring it is not obstructed by the on-screen keyboard on mobile devices.
 * The hook listens for focus events on input and textarea elements and scrolls the focused element
 * into the center of the viewport if it is obstructed. It also handles viewport resize events
 * to ensure the focused element remains in view when the viewport size changes.
 */
export function useScrollInputsIntoView() {
  useEffect(() => {
    document.addEventListener('focusin', handleFocus);
    window.visualViewport?.addEventListener('resize', handleViewportChange);

    return () => {
      document.removeEventListener('focusin', handleFocus);
      window.visualViewport?.removeEventListener(
        'resize',
        handleViewportChange
      );
    };
  }, []);
}
